import type { ReactNode } from "react";
import { SkyBadge, SkyCard, SkyText, type SkyBadgeColor } from "@skypointcloud/skypoint-ui";
import { failureSentence, type SecurityDeliveryRow } from "@shared/types/notifications";
import { SECURITY_CATEGORY_LABELS } from "@shared/types/securityAlerts";
import { useSecurityDeliveries } from "@/hooks/use-security-alerts";
import { deliveryChannel } from "@/lib/notifications/sentences";
import { ErrorState } from "./ScreenState";
import { formatInstant, instantHoverTitle, zoneCaption } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

// copy-jargon-file-ok: an operator setting: what the runner delivers and when. (#929, scripts/check-copy.mjs)

/**
 * Platform settings > General > Security alert deliveries (security alerts design, "UI"; owner
 * answer Q6: PlatformAdmins only in v1). The newest security alerts, one row per delivery, and one
 * row for an alert nobody was subscribed to, so that gap shows. Addresses are masked and an L1
 * address reads "L1 team"; the status is a word and a colour, never a colour alone.
 *
 * A plain table with a caption, as Recent deliveries is (`DeliveriesTable.tsx`), with the library
 * grid's look.
 */

const HEADERS = ["When", "Alert", "Where", "Channel", "To", "Status"] as const;

function where(row: SecurityDeliveryRow): string {
  if (row.tenantName && row.instanceName) return `${row.tenantName} / ${row.instanceName}`;
  return row.tenantName ?? "Whole deployment";
}

/** Why a skipped delivery was not sent. */
function skippedText(
  failureCode: SecurityDeliveryRow["failureCode"],
  channel: NonNullable<SecurityDeliveryRow["channel"]>,
): string {
  if (failureCode && failureCode !== "not_configured") {
    return `Not sent: ${failureSentence(failureCode, channel)}`;
  }
  return channel === "email"
    ? "Not sent: email is not set up for this deployment"
    : "Not sent: no Teams webhook";
}

/** The Status cell: the words and the badge colour. */
export function securityStatus(row: SecurityDeliveryRow): { text: string; color: SkyBadgeColor } {
  if (!row.status || !row.channel) {
    return row.eventStatus === "new"
      ? { text: "Waiting to be sent", color: "informative" }
      : { text: "Not sent: nobody is subscribed", color: "warning" };
  }
  switch (row.status) {
    case "sent":
      return { text: "Sent", color: "success" };
    case "pending":
    case "sending":
      return { text: "Waiting to send", color: "informative" };
    case "held":
      return { text: "Held back", color: "subtle" };
    case "skipped":
      return {
        text: skippedText(row.failureCode, row.channel),
        color: "warning",
      };
    case "dead":
      return {
        text: `Failed: ${failureSentence(row.failureCode ?? "internal", row.channel)}`,
        color: "danger",
      };
    case "parked":
      return {
        text: `Not sent: ${failureSentence(row.failureCode ?? "key_missing", row.channel)}`,
        color: "warning",
      };
  }
}

export function SecurityDeliveriesCard() {
  const zone = useDisplayTz();
  const deliveries = useSecurityDeliveries();
  const rows = deliveries.data;

  function renderDeliveries(): ReactNode {
    if (deliveries.isPending) {
      return (
        <SkyText size={200} color="muted" block role="status">
          Loading security alert deliveries…
        </SkyText>
      );
    }
    if (deliveries.isError) {
      return (
        <ErrorState
          title="Security alert deliveries did not load"
          error={deliveries.error}
          onRetry={() => void deliveries.refetch()}
          testId="security-deliveries-error"
        />
      );
    }
    if (!rows || rows.length === 0) {
      return (
        <SkyText size={300} color="muted" block testId="security-deliveries-empty">
          No security alert has been sent yet.
        </SkyText>
      );
    }
    return (
      <div className="overflow-x-auto">
        <table
          className="w-full border-collapse bg-background text-left"
          data-testid="security-deliveries-grid"
        >
          <caption className="px-3 py-2 text-left">
            <SkyText size={200} color="muted">
              The newest security alerts and where each was sent
            </SkyText>
          </caption>
          <thead>
            <tr>
              {HEADERS.map((header) => (
                <th
                  key={header}
                  scope="col"
                  className="whitespace-nowrap border-b border-border px-4 py-3 text-sm font-semibold text-foreground-soft"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const status = securityStatus(row);
              return (
                <tr
                  key={row.id}
                  className="border-b border-border hover:bg-muted/50"
                  data-testid="security-delivery-row"
                >
                  <td className="px-3 py-2">
                    <SkyText
                      size={200}
                      font="numeric"
                      title={instantHoverTitle(row.createdAt, zone)}
                    >
                      {formatInstant(row.createdAt, zone, "minutes")}
                    </SkyText>
                  </td>
                  <td className="px-3 py-2">
                    <SkyText size={200}>{SECURITY_CATEGORY_LABELS[row.category]}</SkyText>
                  </td>
                  <td className="px-3 py-2">
                    <SkyText size={200}>{where(row)}</SkyText>
                  </td>
                  <td className="px-3 py-2">
                    <SkyText size={200}>
                      {row.channel ? deliveryChannel(row.channel) : "None"}
                    </SkyText>
                  </td>
                  <td className="px-3 py-2">
                    <SkyText size={200}>{row.target?.label ?? "Nobody"}</SkyText>
                  </td>
                  <td className="px-3 py-2">
                    <SkyBadge appearance="tint" color={status.color}>
                      {status.text}
                    </SkyBadge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <SkyCard appearance="outline" testId="security-deliveries-card">
      <section className="flex flex-col gap-3 p-1" aria-labelledby="security-deliveries-heading">
        <div>
          <SkyText as="h2" size={400} weight="semibold" block id="security-deliveries-heading">
            Security alert deliveries
          </SkyText>
          <SkyText size={200} color="muted" block>
            Break-glass, admin grants, destination and secret changes, failed sign-ins and sign-in
            moves, as the runner read them from the audit trail. Admins choose their own in My
            alerts. {zoneCaption(zone)}.
          </SkyText>
        </div>
        {renderDeliveries()}
      </section>
    </SkyCard>
  );
}
